<template>
  <div id="payment-reminder-summary">
    <b-row>
      <b-col xs="12" lg="8">
        <b-row class="extended">
          <b-col xs="12" lg="5" class="col extended">
            <div class="box due-contracts-pie">
              <h3>{{ $t('DUE_CONTRACTS') }}</h3>
              <donut-chart
                v-if="dueContractsDataset"
                :dataset="dueContractsDataset"
                :width="250"
              />
            </div>
          </b-col>
          <b-col xs="12" lg="7">
            <div class="box">
              <h3>{{ $t('PAYMENT_REMINDER_STATUS') }}</h3>
              <bar-chart
                v-if="paymentReminderStatusDatasets"
                :datasets="paymentReminderStatusDatasets"
                :labels="paymentReminderStatusLabels"
                :height="300"
              />
            </div>
          </b-col>
        </b-row>
        <b-row class="extended">
          <b-col xs="12">
            <div class="box late-days-group">
              <table>
                <thead>
                  <tr>
                    <th colspan="2" class="title">{{ $t('LATE_DAYS') }}</th>
                    <th>{{ $t('LOAN_BALANCE') }}</th>
                    <th>{{ $t('TO_RENEW') }}</th>
                    <th>{{ $t('OUT_OF_REACH') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in lateDaysGroupItems" :key="item.text">
                    <td><div class="circle" :class="item.color" /></td>
                    <td>{{ $t(item.text) }}</td>
                    <td>{{ item.loanBalance }}</td>
                    <td>{{ item.toRenew }}</td>
                    <td>{{ item.outOfReach }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-col>
        </b-row>
      </b-col>
      <b-col xs="12" lg="4">
        <div class="box summary-info">
          <h3>{{ $t('SUMMARY_INFO') }}</h3>
          <ul>
            <li v-for="item in summaryItems" :key="item.text">
              <div class="icon-wrapper" :class="item.color">
                <font-awesome-icon :icon="item.icon" />
              </div>
              <div class="text">
                <span>{{ item.val }}</span>
                <span v-html="'&nbsp;'" />
                <span>{{ $t(item.text) }}</span>
              </div>
              <div class="subtext">{{ $t(item.subtext) }}</div>
            </li>
          </ul>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { computed } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPhoneAlt, faExclamationCircle, faStopwatch, faSync, faMoneyBill, faPiggyBank } from '@fortawesome/free-solid-svg-icons';

import { addCommas } from '@/helpers';
import COLORS from '@/consts/colors';

import BarChart from '@/components/BarChart';
import DonutChart from '@/components/DonutChart';

const { useState } = createNamespacedHelpers('paymentReminder');

export default {
  name: 'PaymentReminderSummary',
  components: {
    FontAwesomeIcon,
    BarChart,
    DonutChart
  },
  setup() {
    const {
      numberOfPastDueClients,
      numberOfDueContracts,
      numberOfRenewedContracts,
      numberOfRenewedContractsWithCash,
      numberOfRenewedContractsWithBank,
      numberOfAppointments,
      paymentReminderStatus
    } = useState([
      'numberOfPastDueClients',
      'numberOfDueContracts',
      'numberOfRenewedContracts',
      'numberOfRenewedContractsWithCash',
      'numberOfRenewedContractsWithBank',
      'numberOfAppointments',
      'paymentReminderStatus'
    ]);

    const summaryItems = computed(() => [
      {
        val: numberOfPastDueClients.value,
        icon: faPhoneAlt,
        text: 'CLIENTS',
        subtext: 'NEED_PAYMENT_REMINDER',
        color: 'yellow'
      },
      {
        val: numberOfDueContracts.value,
        icon: faExclamationCircle,
        text: 'DUE_CONTRACTS',
        subtext: 'NEED_ATTENTION',
        color: 'red'
      },
      {
        val: numberOfAppointments.value,
        icon: faStopwatch,
        text: 'TO_RENEW',
        subtext: 'SCHEDULE',
        color: 'blue'
      },
      {
        val: numberOfRenewedContracts.value,
        icon: faSync,
        text: 'RENEWED_CONTRACTS',
        subtext: 'COOOOOL',
        color: 'green'
      },
      {
        val: numberOfRenewedContractsWithCash.value,
        icon: faMoneyBill,
        text: 'RENEWED_CONTRACTS_WITH_CASH',
        subtext: 'COOOOOL',
        color: 'green'
      },
      {
        val: numberOfRenewedContractsWithBank.value,
        icon: faPiggyBank,
        text: 'RENEWED_CONTRACTS_WITH_BANK',
        subtext: 'COOOOOL',
        color: 'green'
      }
    ]);

    const lateDaysGroupItems = computed(() => [
      {
        color: '',
        text: 'TODAY',
        loanBalance: addCommas(paymentReminderStatus.value.today.loanBalance),
        toRenew: paymentReminderStatus.value.today.appointments,
        outOfReach: paymentReminderStatus.value.today.outOfReach
      },
      {
        color: 'blue',
        text: '1_DAY',
        loanBalance: addCommas(paymentReminderStatus.value.late1Day.loanBalance),
        toRenew: paymentReminderStatus.value.late1Day.appointments,
        outOfReach: paymentReminderStatus.value.late1Day.outOfReach
      },
      {
        color: 'green',
        text: '2_3_DAYS',
        loanBalance: addCommas(paymentReminderStatus.value.late2To3Days.loanBalance),
        toRenew: paymentReminderStatus.value.late2To3Days.appointments,
        outOfReach: paymentReminderStatus.value.late2To3Days.outOfReach
      },
      {
        color: 'yellow',
        text: '4_5_DAYS',
        loanBalance: addCommas(paymentReminderStatus.value.late4To5Days.loanBalance),
        toRenew: paymentReminderStatus.value.late4To5Days.appointments,
        outOfReach: paymentReminderStatus.value.late4To5Days.outOfReach
      },
      {
        color: 'red',
        text: 'OVER_5_DAYS',
        loanBalance: addCommas(paymentReminderStatus.value.lateOver5Days.loanBalance),
        toRenew: paymentReminderStatus.value.lateOver5Days.appointments,
        outOfReach: paymentReminderStatus.value.lateOver5Days.outOfReach
      }
    ]);

    const paymentReminderStatusDatasets = computed(() => [
      {
        label: 'TO_RENEW',
        color: COLORS.BLUE_NCS,
        data: [
          paymentReminderStatus.value.today.appointments,
          paymentReminderStatus.value.late1Day.appointments,
          paymentReminderStatus.value.late2To3Days.appointments,
          paymentReminderStatus.value.late4To5Days.appointments,
          paymentReminderStatus.value.lateOver5Days.appointments
        ]
      },
      {
        label: 'OUT_OF_REACH',
        color: COLORS.YELLOW_CRAYOLA,
        data: [
          paymentReminderStatus.value.today.outOfReach,
          paymentReminderStatus.value.late1Day.outOfReach,
          paymentReminderStatus.value.late2To3Days.outOfReach,
          paymentReminderStatus.value.late4To5Days.outOfReach,
          paymentReminderStatus.value.lateOver5Days.outOfReach
        ]
      }
    ]);

    const dueContractsDataset = computed(() => [
      {
        label: 'TODAY',
        color: COLORS.INDEPENDENCE,
        value: paymentReminderStatus.value.today.total
      },
      {
        label: '1_DAY',
        color: COLORS.BLUE_NCS,
        value: paymentReminderStatus.value.late1Day.total
      },
      {
        label: '2_3_DAYS',
        color: COLORS.SHAMROCK_GREEN,
        value: paymentReminderStatus.value.late2To3Days.total
      },
      {
        label: '4_5_DAYS',
        color: COLORS.YELLOW_CRAYOLA,
        value: paymentReminderStatus.value.late4To5Days.total
      },
      {
        label: 'OVER_5_DAYS',
        color: COLORS.BRIGHT_MAROON,
        value: paymentReminderStatus.value.lateOver5Days.total
      }
    ]);

    return {
      summaryItems,
      lateDaysGroupItems,
      paymentReminderStatusDatasets,
      paymentReminderStatusLabels: [
        'TODAY',
        '1_DAY',
        '2_3_DAYS',
        '4_5_DAYS',
        'OVER_5_DAYS'
      ],
      dueContractsDataset
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

#payment-reminder-summary {
  .row.extended {
    margin-right: -30px;
  }

  .col.extended {
    padding-right: 0;
  }

  .circle {
    width: 15px;
    height: 15px;
    border: 3.5px solid $independence;
    border-radius: 50%;
    display: inline-block;

    &.red {
      border-color: $brightMaroon;
    }

    &.yellow {
      border-color: $yellowCrayola;
    }

    &.green {
      border-color: $shamrockGreen;
    }

    &.blue {
      border-color: $blueNcs;
    }
  }

  .box {
    background-color: $babyPowder;
    border: 1px solid $alto;
    border-radius: 7px;
    padding: 15px;
    padding-top: 20px;
    padding-bottom: 10px;

    h3 {
      font-size: 18px;
      font-weight: bold;
      margin-bottom: 20px;
    }
  }

  .summary-info {
    height: 100%;

    ul {
      padding: 0;
      margin: 0;
      list-style: none;

      li {
        padding: 25px 7px;
        border-bottom: 1px solid $alto;
        display: flex;
        align-content: center;
        cursor: default;

        &:last-child {
          border: 0;
        }

        .icon-wrapper {
          padding-top: 8px;
          min-width: 38px;
          width: 38px;
          height: 38px;
          color: $independence;
          position: relative;
          text-align: center;
          transition: all .2s ease-in-out;
          z-index: 2;

          &::before {
            content: "";
            background-color: $independence;
            opacity: .3;
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border-radius: 50%;
            transition: all .2s ease-in-out;
            z-index: -1;
          }

          &.yellow {
            color: $yellowCrayola;

            &::before {
              background-color: $yellowCrayola;
            }
          }

          &.red {
            color: $brightMaroon;

            &::before {
              background-color: $brightMaroon;
            }
          }

          &.blue {
            color: $blueNcs;

            &::before {
              background-color: $blueNcs;
            }
          }

          &.green {
            color: $shamrockGreen;

            &::before {
              background-color: $shamrockGreen;
            }
          }
        }

        .text {
          line-height: 38px;
          padding-left: 10px;
        }

        .subtext {
          font-size: 13px;
          color: $lavenderGrey;
          line-height: 38px;
          margin-left: auto;
        }
      }
    }
  }

  .late-days-group {
    margin-top: 15px;

    table {
      width: 100%;
      border-collapse: inherit;
      border-spacing: 0;

      thead {
        th {
          text-align: center;
          font-weight: normal;
          text-transform: capitalize;
          font-size: 13px;
          color: $lavenderGrey;
          padding: 7px 5px;

          &.title {
            font-size: 16px;
            font-weight: bold;
            text-align: left;
            color: $independence;
          }
        }
      }

      tbody {
        tr {
          td {
            padding: 7px 5px;
            text-align: center;
            font-size: 13px;

            .circle {
              vertical-align: middle;
              margin-top: -2px;
            }

            &:first-child {
              width: 20px;
              text-align: left;
            }

            &:nth-child(2) {
              text-align: left;
            }
          }
        }
      }
    }
  }

  .due-contracts-pie {
    height: 100%;
  }
}
</style>
